import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { pick, asList, fmtDateTime } from "@/lib/admin-utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, PlayCircle, Eye, Loader2 } from "lucide-react";

const TABS = [
  { id: "pending", label: "In attesa", match: ["pending", "in_attesa", "unverified"] },
  { id: "approved", label: "Approvati", match: ["verified", "approved", "approvato", "active"] },
  { id: "rejected", label: "Rifiutati", match: ["rejected", "rifiutato"] },
  { id: "suspended", label: "Sospesi", match: ["suspended", "sospeso"] },
];

export function AdminProfessionals() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [viewTarget, setViewTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-pros"],
    queryFn: async () => (await api.get("/admin/professionals")).data,
  });

  const pros = asList(data);
  const statusOf = (p) => String(pick(p, ["verification_status", "status"], "")).toLowerCase();
  const filtered = pros.filter((p) => TABS.find((t) => t.id === tab).match.includes(statusOf(p)));

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      qc.invalidateQueries({ queryKey: ["admin-pros"] });
      qc.invalidateQueries({ queryKey: ["admin-kpi"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const approve = (p) => act(() => api.post(`/admin/professionals/${p.user_id || p.id}/verify`, { status: "verified" }), "Professionista approvato: account operativo su Web e app.");
  const reject = () => act(async () => {
    await api.post(`/admin/professionals/${rejectTarget.user_id || rejectTarget.id}/verify`, { status: "rejected", reason });
    setRejectTarget(null);
    setReason("");
  }, "Professionista rifiutato.");
  const suspend = (p) => act(() => api.post(`/admin/users/${p.user_id || p.id}/status`, { status: "suspended" }), "Account sospeso.");
  const reactivate = (p) => act(() => api.post(`/admin/users/${p.user_id || p.id}/status`, { status: "active" }), "Account riattivato.");

  return (
    <div className="px-4 sm:px-6 pt-5 space-y-4" data-testid="admin-pros-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Professionisti</h1>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => {
          const count = pros.filter((p) => t.match.includes(statusOf(p))).length;
          return (
            <button key={t.id} data-testid={`pros-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors active:scale-95 ${
                tab === t.id ? "bg-whoo text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}>
              {t.label} ({count})
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-3xl" />)}</div>
      ) : filtered.length ? (
        <div className="space-y-3" data-testid="pros-list">
          {filtered.map((p) => {
            const id = p.user_id || p.id;
            return (
              <div key={id} data-testid={`pro-card-${id}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{pick(p, ["name", "business_name", "full_name"], "Professionista")}</p>
                    <p className="text-xs text-slate-500 truncate">{pick(p, ["email"], "—")}{pick(p, ["phone"]) ? ` · ${p.phone}` : ""}</p>
                    <p className="text-xs text-slate-500 capitalize">
                      {(pick(p, ["categories"], []) || []).join(" · ") || "—"} · {pick(p, ["city", "zone"], "—")}
                    </p>
                    <p className="text-xs text-slate-400">Registrato il {fmtDateTime(pick(p, ["created_at", "registered_at"]))}</p>
                  </div>
                  <Button data-testid={`view-pro-${id}`} size="sm" variant="ghost" onClick={() => setViewTarget(p)} className="shrink-0">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tab === "pending" && (
                    <>
                      <Button data-testid={`approve-pro-${id}`} size="sm" disabled={busy} onClick={() => approve(p)}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Approva</>}
                      </Button>
                      <Button data-testid={`reject-pro-${id}`} size="sm" variant="outline" disabled={busy} onClick={() => setRejectTarget(p)}
                        className="rounded-full text-red-600 border-red-200 font-semibold">
                        <XCircle className="w-4 h-4 mr-1" /> Rifiuta
                      </Button>
                    </>
                  )}
                  {tab !== "suspended" && tab !== "pending" && (
                    <Button data-testid={`suspend-pro-${id}`} size="sm" variant="outline" disabled={busy} onClick={() => suspend(p)}
                      className="rounded-full font-semibold"><PauseCircle className="w-4 h-4 mr-1" /> Sospendi</Button>
                  )}
                  {tab === "suspended" && (
                    <Button data-testid={`reactivate-pro-${id}`} size="sm" disabled={busy} onClick={() => reactivate(p)}
                      className="rounded-full bg-volt hover:bg-volt/90 text-white font-semibold"><PlayCircle className="w-4 h-4 mr-1" /> Riattiva</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 text-center" data-testid="pros-empty">
          <p className="text-sm text-slate-500">Nessun professionista in questa categoria.</p>
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-heading">Rifiuta {pick(rejectTarget, ["name", "email"], "professionista")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="reject-reason-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              className="bg-slate-50 border-transparent rounded-2xl resize-none" placeholder="Motivazione del rifiuto (verrà registrata)..." />
            <Button data-testid="reject-confirm-btn" onClick={reject} disabled={busy || !reason.trim()}
              className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Conferma rifiuto"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewTarget} onOpenChange={(o) => !o && setViewTarget(null)}>
        <DialogContent className="max-w-md rounded-3xl max-h-[80vh] overflow-y-auto" data-testid="pro-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading">Profilo professionista</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            {viewTarget && Object.entries(viewTarget).filter(([k]) => !k.startsWith("_")).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-slate-50 pb-1.5">
                <span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</span>
                <span className="text-slate-900 text-right break-all">{typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
